/**
 * Devanagari display names for engine ids on Hindi result screens.
 *
 * The engine returns Latin ids ("purva_phalguni", "mars", "fire"). Hindi pages
 * used to print those ids as-is. This is a small hand-kept map instead of the
 * @ishvaram/domain-data nakshatra dataset (~32 KB) so tool chunks stay light;
 * vedic-names-hi.test.ts pins every entry to the canonical name_hi.
 */
import { planets } from "@ishvaram/domain-data/planets";

/** @internal */
export const NAKSHATRA_NAME_HI: Readonly<Record<string, string>> = {
  ashwini: "अश्विनी",
  bharani: "भरणी",
  krittika: "कृत्तिका",
  rohini: "रोहिणी",
  mrigashira: "मृगशिरा",
  ardra: "आर्द्रा",
  punarvasu: "पुनर्वसु",
  pushya: "पुष्य",
  ashlesha: "आश्लेषा",
  magha: "मघा",
  purva_phalguni: "पूर्व फाल्गुनी",
  uttara_phalguni: "उत्तर फाल्गुनी",
  hasta: "हस्त",
  chitra: "चित्रा",
  swati: "स्वाति",
  vishakha: "विशाखा",
  anuradha: "अनुराधा",
  jyeshtha: "ज्येष्ठा",
  moola: "मूल",
  purva_ashadha: "पूर्वाषाढ़ा",
  uttara_ashadha: "उत्तराषाढ़ा",
  shravana: "श्रवण",
  dhanishta: "धनिष्ठा",
  shatabhisha: "शतभिषा",
  purva_bhadrapada: "पूर्व भाद्रपद",
  uttara_bhadrapada: "उत्तर भाद्रपद",
  revati: "रेवती",
};

const NAKSHATRA_ALIASES: Readonly<Record<string, string>> = {
  mula: "moola",
  shatabhishaj: "shatabhisha",
  dhanishtha: "dhanishta",
};

const ELEMENT_NAME_HI: Readonly<Record<string, string>> = {
  fire: "अग्नि",
  earth: "पृथ्वी",
  air: "वायु",
  water: "जल",
};

const PLANET_NAME_HI: Readonly<Record<string, string>> = Object.fromEntries(
  planets.flatMap((planet) => [
    [planet.id, planet.name_hi],
    [planet.name_en.toLowerCase(), planet.name_hi],
  ])
);

function key(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/** Hindi nakshatra name for an engine id or English name; null when unknown. */
export function nakshatraNameHi(value?: string | null): string | null {
  if (!value) return null;
  const id = key(value);
  return NAKSHATRA_NAME_HI[NAKSHATRA_ALIASES[id] ?? id] ?? null;
}

/** Hindi graha name for a planet id ("mars") or English name ("Mars"); null when unknown. */
export function planetNameHi(value?: string | null): string | null {
  if (!value) return null;
  return PLANET_NAME_HI[key(value)] ?? null;
}

/** Hindi tattva name for fire/earth/air/water (any case); null when unknown. */
export function elementNameHi(value?: string | null): string | null {
  if (!value) return null;
  return ELEMENT_NAME_HI[key(value)] ?? null;
}

/**
 * Devanagari weekday for the engine's vara name. The engine's `vara.name_hi`
 * is romanised ("Shukravaar"), which Hindi pages printed as-is.
 */
const VARA_NAME_HI: Readonly<Record<string, string>> = {
  ravivaar: "रविवार",
  sunday: "रविवार",
  somvaar: "सोमवार",
  monday: "सोमवार",
  mangalvaar: "मंगलवार",
  tuesday: "मंगलवार",
  budhvaar: "बुधवार",
  wednesday: "बुधवार",
  guruvaar: "गुरुवार",
  thursday: "गुरुवार",
  shukravaar: "शुक्रवार",
  friday: "शुक्रवार",
  shanivaar: "शनिवार",
  saturday: "शनिवार",
};

/** Hindi weekday for an engine vara name (romanised or English); null when unknown. */
export function varaNameHi(value?: string | null): string | null {
  if (!value) return null;
  return VARA_NAME_HI[key(value)] ?? null;
}
